import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { icons, ArrowRight, Loader2, MapPin, Sparkles, Search, Tag } from "lucide-react";
import api from "@/lib/api";
import { buildRequestUrl, contextForSlug, getCategories, getSavedLocation, setSavedLocation, trackHome } from "@/lib/requestContext";
import { SectionHeader } from "@/components/marketing/ContentSections";
import { HeroCollage, MobileHeroPhoto, QuickChips, PhotoCard } from "@/components/marketing/RequestPremium";

const cx = (...c) => c.filter(Boolean).join(" ");
const TONES = { white: "bg-white", light: "bg-slate-50", sand: "bg-[#f5f0e8]", dark: "bg-[#071b22] text-white" };
const pascal = (n) => String(n || "").split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join("");
export const DynIcon = ({ name, className }) => { const C = icons[pascal(name)] || icons.Sparkles; return <C className={className} />; };

let settingsPromise = null;
const getHomeSettings = () => (settingsPromise = settingsPromise || api.get("/homepage/active").then((r) => r.data).catch(() => ({ category_click: "request" })));

function useRotating(list, ms = 2600) {
  const [i, setI] = useState(0);
  useEffect(() => { if (!list?.length) return; const t = setInterval(() => setI((x) => (x + 1) % list.length), ms); return () => clearInterval(t); }, [list, ms]);
  return list?.[i] || "";
}

export function ServiceAssistant({ source = "homepage_hero", placeholders, placeholder, buttonLabel = "Get Estimates", askLocation, dark, multiline, stacked, testid = "assistant" }) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const [zip, setZip] = useState(getSavedLocation().zip || "");
  const [cats, setCats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [choosing, setChoosing] = useState(false);
  const rotating = useRotating(placeholders);
  useEffect(() => { getCategories().then(setCats); }, []);
  const byId = useMemo(() => Object.fromEntries(cats.map((c) => [c.id, c])), [cats]);

  const suggestions = useMemo(() => {
    const t = text.trim().toLowerCase();
    if (t.length < 3 || result) return [];
    const parent = cats.find((c) => !c.parent_id && c.name.toLowerCase() === t);
    if (parent) return cats.filter((c) => c.parent_id === parent.id).slice(0, 6);
    return cats.filter((c) => c.name.toLowerCase().includes(t)).sort((a, b) => Number(!!b.parent_id) - Number(!!a.parent_id)).slice(0, 6);
  }, [text, cats, result]);

  const go = (cat, extra = {}) => {
    trackHome("request_click", { source, category: cat?.slug });
    const ctx = cat?.parent_id ? { category_id: cat.parent_id, subcategory_id: cat.id } : { category_id: cat?.id };
    navigate(buildRequestUrl({ ...ctx, request_source: source, homepage_component: source, ...extra }));
  };
  const pick = (cat) => { trackHome("category_select", { source, category: cat.slug }); go(cat, text.trim().length > 12 ? { search_text: text.trim() } : {}); };
  const identify = async (e) => {
    e?.preventDefault();
    if (text.trim().length < 5) return;
    setBusy(true); setChoosing(false); trackHome("ai_request", { source });
    try { const { data } = await api.post("/service-requests/identify-public", { text }); setResult(data); }
    catch (_) { setResult({ category: null }); } finally { setBusy(false); }
  };
  const target = result?.subcategory || result?.category;
  const field = cx("w-full bg-transparent outline-none text-base md:text-lg placeholder:text-slate-400", dark ? "text-[#071b22]" : "text-[#071b22]");

  return (
    <div className="w-full" data-testid={testid}>
      <form onSubmit={identify} className="rounded-[1.75rem] bg-white p-2 shadow-[0_30px_60px_-25px_rgba(7,27,34,0.45)] ring-1 ring-black/5">
        <div className={cx("flex gap-2", multiline || stacked ? "flex-col" : "flex-col md:flex-row md:items-center")}>
          <div className="flex flex-1 items-start gap-3 px-4 py-3">
            <Search className="h-5 w-5 mt-1 shrink-0 text-[#165a71]" />
            {multiline
              ? <textarea rows={3} value={text} onChange={(e) => { setText(e.target.value); setResult(null); }} placeholder={placeholder} className={cx(field, "resize-none leading-relaxed")} data-testid={`${testid}-input`} />
              : <input value={text} onChange={(e) => { setText(e.target.value); setResult(null); }} placeholder={rotating || placeholder || "What do you need done?"} className={field} data-testid={`${testid}-input`} />}
          </div>
          <div className={stacked ? "flex items-center gap-2 border-t border-slate-100 pt-2" : "contents"}>
          {askLocation && (
            <div className={cx("flex items-center gap-2 px-4 py-3", stacked ? "flex-1" : "md:w-40 md:border-l border-t md:border-t-0 border-slate-100")}>
              <MapPin className="h-4 w-4 text-slate-400" />
              <input value={zip} onChange={(e) => { const z = e.target.value.replace(/\D/g, "").slice(0, 5); setZip(z); setSavedLocation({ zip: z }); }} placeholder="ZIP code" inputMode="numeric" className="w-full bg-transparent outline-none text-sm text-[#071b22]" data-testid={`${testid}-zip`} />
            </div>
          )}
          <button type="submit" disabled={busy || text.trim().length < 5} className="inline-flex items-center justify-center gap-2 rounded-[1.25rem] bg-[#165a71] px-7 py-4 text-base font-semibold text-white shadow-lg shadow-[#165a71]/30 transition-[background-color,transform] hover:bg-[#0f4657] active:scale-[0.98] disabled:opacity-60" data-testid={`${testid}-submit`}>
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : <Sparkles className="h-5 w-5" />} {buttonLabel}
          </button>
          </div>
        </div>
      </form>

      {suggestions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid={`${testid}-suggestions`}>
          {suggestions.map((c) => (
            <button key={c.id} onClick={() => pick(c)} className={cx("inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-sm transition-colors", dark ? "bg-white/10 text-white hover:bg-white/20" : "bg-[#165a71]/10 text-[#165a71] hover:bg-[#165a71]/20")} data-testid={`${testid}-suggestion-${c.slug}`}>
              {c.name}{c.parent_id && byId[c.parent_id] && <span className="opacity-60">· {byId[c.parent_id].name}</span>}
            </button>
          ))}
        </div>
      )}

      {result && (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-4 rounded-2xl bg-white p-5 text-[#071b22] shadow-xl ring-1 ring-black/5" data-testid={`${testid}-result`}>
          {target ? (
            <>
              <div className="text-sm text-slate-500">It sounds like you need</div>
              <div className="mt-0.5 font-display text-xl font-semibold" data-testid={`${testid}-result-name`}>{target.name}{result.subcategory && <span className="text-slate-400 font-normal text-base"> · {result.category.name}</span>}</div>
              <div className="mt-4 flex flex-wrap gap-2">
                <button onClick={() => go(target, { search_text: text.trim(), request_source: source === "homepage_hero" ? "ai_search" : source })} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0f4657]" data-testid={`${testid}-continue`}>Continue <ArrowRight className="h-4 w-4" /></button>
                <button onClick={() => setChoosing((c) => !c)} className="rounded-full border border-slate-200 px-5 py-2.5 text-sm font-medium hover:border-[#165a71]" data-testid={`${testid}-choose-another`}>Choose Another Service</button>
              </div>
            </>
          ) : <div className="text-sm">We couldn't pin that down — pick the closest service below.</div>}
          {(choosing || !target) && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid={`${testid}-alternatives`}>
              {[...(result.alternatives || []), ...cats.filter((c) => !c.parent_id)].filter((c, i, a) => a.findIndex((x) => x.id === c.id) === i).slice(0, 14).map((c) => (
                <button key={c.id} onClick={() => go(c, { search_text: text.trim() })} className="rounded-full bg-slate-100 px-3.5 py-1.5 text-sm hover:bg-[#165a71]/10" data-testid={`${testid}-alt-${c.slug}`}>{c.name}</button>
              ))}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}

export function RequestHero({ props = {}, mode }) {
  const { eyebrow, headline, body, placeholders, primary_label, secondary_cta, ask_location, trust_text, image, request_source, layout, images = [], preview, chips = [], highlights = [] } = props;
  const [sticky, setSticky] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const on = () => setSticky(ref.current ? ref.current.getBoundingClientRect().bottom < 0 : false);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const split = layout === "split";
  return (
    <section ref={ref} className="relative -mt-24 pt-24 overflow-hidden bg-[#071b22] text-white" data-testid="request-hero">
      {image && <img src={image} alt="" className={cx("absolute inset-0 h-full w-full object-cover", split ? "opacity-[0.12] blur-sm scale-105" : "opacity-30")} />}
      <div className="absolute inset-0 bg-gradient-to-r from-[#071b22] via-[#071b22]/90 to-[#071b22]/50" aria-hidden />
      <div className="absolute inset-0 gcw-noise opacity-30" aria-hidden />
      {split && <div className="absolute -left-40 top-1/3 h-[30rem] w-[30rem] rounded-full bg-[#165a71]/35 blur-3xl" aria-hidden />}
      <div className={cx("relative mx-auto px-5 lg:px-10 pt-10 pb-16 md:pt-16 md:pb-24", split ? "max-w-7xl grid gap-10 lg:grid-cols-[1.05fr_1fr] items-center" : "max-w-5xl md:pt-20 md:pb-28")}>
      <div>
        {eyebrow && <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 backdrop-blur px-3.5 py-1.5 text-xs font-medium text-white/80"><span className="h-1.5 w-1.5 rounded-full bg-[#5fd3c6]" />{eyebrow}</div>}
        <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]" data-testid="request-hero-headline">{headline}</motion.h1>
        {body && <p className="mt-5 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed">{body}</p>}
        <div className="mt-8 max-w-4xl"><ServiceAssistant source={request_source || "homepage_hero"} placeholders={placeholders} buttonLabel={primary_label} askLocation={ask_location} stacked={split} dark testid="hero-assistant" /></div>
        <QuickChips chips={chips} source={request_source || "homepage_hero"} mode={mode} />
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
          {secondary_cta?.label && (mode === "edit" ? <span className="font-medium text-white/80 underline underline-offset-4">{secondary_cta.label}</span>
            : <Link to={secondary_cta.href || "/services"} className="font-medium text-white/80 underline underline-offset-4 hover:text-white" data-testid="request-hero-secondary">{secondary_cta.label}</Link>)}
          {trust_text && <span className="text-white/50">{trust_text}</span>}
        </div>
        {highlights.length > 0 && (
          <div className="mt-8 grid grid-cols-3 gap-3 max-w-xl border-t border-white/10 pt-6" data-testid="hero-highlights">
            {highlights.map((h, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <DynIcon name={h.icon} className="h-4 w-4 mt-0.5 shrink-0 text-[#5fd3c6]" />
                <div><div className="text-sm font-semibold leading-tight">{h.title}</div><div className="text-xs text-white/50 leading-snug">{h.body}</div></div>
              </div>
            ))}
          </div>
        )}
        {split && <MobileHeroPhoto image={images[0]} preview={preview} />}
      </div>
      {split && <HeroCollage images={images} preview={preview} />}
      </div>
      {sticky && mode !== "edit" && (
        <div className="md:hidden fixed bottom-4 inset-x-4 z-40">
          <button onClick={() => { window.scrollTo({ top: 0, behavior: "smooth" }); setTimeout(() => document.querySelector("[data-testid='hero-assistant-input']")?.focus(), 450); }} className="w-full rounded-full bg-[#165a71] py-4 text-base font-semibold text-white shadow-2xl" data-testid="sticky-request-cta">Request a Service</button>
        </div>
      )}
    </section>
  );
}

function CardMedia({ img, icon }) {
  const [bad, setBad] = useState(false);
  return (
    <div className="aspect-[4/3] overflow-hidden bg-gradient-to-br from-[#165a71]/15 via-[#87bccc]/15 to-[#f5f0e8] grid place-items-center">
      {img && !bad ? <img src={img} alt="" loading="lazy" onError={() => setBad(true)} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
        : <DynIcon name={icon} className="h-12 w-12 text-[#165a71]/70 transition-transform duration-500 group-hover:scale-110" />}
    </div>
  );
}

function hrefFor(item, cat, settings, source) {
  const b = item.click_behavior === "default" || !item.click_behavior ? (settings.category_click === "directory" ? "directory" : "request") : item.click_behavior;
  if (b === "url") return { href: item.url || "/" };
  if (b === "directory") return { href: `/services?category=${item.category_slug || ""}` };
  if (b === "category") return { href: `/categories/${item.category_slug || ""}` };
  if (b === "providers") return { href: `/providers?category_id=${cat?.id || ""}` };
  return { request: true };
}

export function RequestServiceGrid({ props = {}, mode }) {
  const { eyebrow, headline, lede, items = [], data_source = "custom", limit = 12, columns = 4, card_style = "icon", tone = "white", request_source = "category", mobile_slider = true, cta, layout } = props;
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [popular, setPopular] = useState([]);
  const [settings, setSettings] = useState({ category_click: "request" });
  useEffect(() => { getCategories().then(setCats); getHomeSettings().then(setSettings); }, []);
  useEffect(() => { if (data_source === "popular") api.get("/request-entry/popular", { params: { limit } }).then((r) => setPopular(r.data)).catch(() => {}); }, [data_source, limit]);
  const bySlug = useMemo(() => Object.fromEntries(cats.map((c) => [c.slug, c])), [cats]);
  const list = useMemo(() => {
    const asItem = (c) => ({ label: c.name, category_slug: c.slug, icon: c.icon, image: c.image, description: c.requests ? `${c.requests} recent request${c.requests === 1 ? "" : "s"}` : null, click_behavior: "default" });
    if (data_source === "popular") return popular.map(asItem);
    if (data_source === "featured") return cats.filter((c) => c.is_featured).slice(0, limit).map(asItem);
    if (data_source === "digital" || data_source === "local") return cats.filter((c) => !c.parent_id && c.kind === data_source).slice(0, limit).map(asItem);
    if (data_source === "categories") return cats.filter((c) => !c.parent_id).slice(0, limit).map(asItem);
    return items.slice(0, limit || items.length);
  }, [data_source, popular, cats, items, limit]);
  if (!list.length && mode !== "edit") return null;
  const dark = tone === "dark";
  const grid = { 3: "md:grid-cols-3", 4: "md:grid-cols-3 lg:grid-cols-4", 5: "md:grid-cols-4 lg:grid-cols-5", 6: "md:grid-cols-4 lg:grid-cols-6" }[columns] || "md:grid-cols-4";

  const click = async (item) => {
    if (mode === "edit") return;
    const cat = bySlug[item.category_slug];
    const h = hrefFor(item, cat, settings, request_source);
    trackHome("category_select", { source: request_source, category: item.category_slug });
    if (h.href) { navigate(h.href); return; }
    const ctx = await contextForSlug(item.category_slug);
    navigate(buildRequestUrl({ ...ctx, request_source, homepage_component: headline || request_source, urgency: item.urgency, deal_id: item.deal_id, campaign: item.campaign }));
  };

  return (
    <section className={cx(TONES[tone] || TONES.white, "relative py-16 md:py-24")} data-testid={`request-grid-${request_source}`}>
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <SectionHeader eyebrow={eyebrow} headline={headline} lede={lede} dark={dark} />
        <div className={cx("mt-10", mobile_slider ? "flex md:grid gap-4 overflow-x-auto snap-x snap-mandatory pb-2 -mx-5 px-5 md:mx-0 md:px-0 md:overflow-visible" : "grid grid-cols-2 gap-4", grid, card_style === "photo" && layout === "bento" && "md:grid-flow-dense md:auto-rows-[240px]")}>
          {list.map((it, i) => {
            const cat = bySlug[it.category_slug];
            if (card_style === "photo") return <PhotoCard key={`${it.category_slug}-${i}`} item={{ ...it, icon: it.icon || cat?.icon }} img={it.image || cat?.image} big={layout === "bento" && i === 0} dark={dark} i={i} onClick={() => click(it)} testid={`request-card-${request_source}-${it.category_slug}${it.urgency ? "-urgent" : ""}`} />;
            const img = it.image || (card_style === "image" ? cat?.image : null);
            return (
              <motion.button key={`${it.category_slug}-${i}`} type="button" onClick={() => click(it)} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: Math.min(i, 8) * 0.04 }}
                className={cx("group relative snap-start shrink-0 w-[44vw] sm:w-[30vw] md:w-auto text-left overflow-hidden rounded-3xl transition-[transform,box-shadow] duration-300 hover:-translate-y-1",
                  card_style === "urgent" ? "bg-white ring-1 ring-rose-200 hover:shadow-[0_20px_40px_-20px_rgba(225,29,72,0.4)]" : dark ? "bg-white/[0.05] ring-1 ring-white/10 hover:bg-white/[0.09]" : "bg-white ring-1 ring-slate-200/80 hover:shadow-[0_24px_48px_-24px_rgba(7,27,34,0.3)]")}
                data-testid={`request-card-${request_source}-${it.category_slug}${it.urgency ? "-urgent" : ""}`}>
                {card_style === "image" && <CardMedia img={img} icon={it.icon || cat?.icon} />}
                <div className="p-5">
                  {card_style !== "image" && <div className={cx("h-11 w-11 rounded-2xl grid place-items-center mb-4", card_style === "urgent" ? "bg-rose-50 text-rose-600" : dark ? "bg-[#87bccc]/15 text-[#87bccc]" : "bg-[#165a71]/10 text-[#165a71]")}><DynIcon name={it.icon || cat?.icon} className="h-5 w-5" /></div>}
                  <div className={cx("font-display text-base md:text-lg font-semibold leading-snug", dark ? "text-white" : "text-[#071b22]")}>{it.label}</div>
                  {it.description && <div className={cx("mt-1 text-xs md:text-sm", dark ? "text-white/55" : "text-slate-500")}>{it.description}</div>}
                  <div className={cx("mt-3 inline-flex items-center gap-1 text-xs font-semibold", card_style === "urgent" ? "text-rose-600" : dark ? "text-[#87bccc]" : "text-[#165a71]")}>{card_style === "urgent" ? "Get help now" : "Get estimates"} <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></div>
                </div>
              </motion.button>
            );
          })}
          {!list.length && <div className="text-sm text-slate-400">No items yet — add items or change the data source in the editor.</div>}
        </div>
        {cta?.label && <div className="mt-10">{mode === "edit" ? <span className="text-sm font-semibold text-[#165a71]">{cta.label}</span> : <Link to={cta.href || "/services"} className="inline-flex items-center gap-2 rounded-full border border-current/20 px-6 py-3 text-sm font-semibold text-[#165a71] ring-1 ring-[#165a71]/20 hover:bg-[#165a71]/5" data-testid={`request-grid-cta-${request_source}`}>{cta.label} <ArrowRight className="h-4 w-4" /></Link>}</div>}
      </div>
    </section>
  );
}

export function AiGuidedRequest({ props = {} }) {
  const { eyebrow, headline, body, placeholder, button_label, request_source = "ai_search", image } = props;
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-[#0d3644] to-[#071b22] py-20 md:py-28 text-white" data-testid="ai-guided-request">
      {image && <img src={image} alt="" loading="lazy" className="absolute inset-y-0 right-0 hidden lg:block h-full w-1/2 object-cover opacity-25 [mask-image:linear-gradient(to_left,black,transparent)]" />}
      <div className="absolute -left-32 top-10 h-96 w-96 rounded-full bg-[#5fd3c6]/15 blur-3xl" aria-hidden />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-10 grid gap-10 lg:grid-cols-[1fr_1.2fr] items-center">
        <SectionHeader eyebrow={eyebrow} headline={headline} lede={body} dark />
        <ServiceAssistant source={request_source} placeholder={placeholder} buttonLabel={button_label} multiline dark testid="guided-assistant" />
      </div>
    </section>
  );
}

export function RequestDeals({ props = {}, mode }) {
  const { eyebrow, headline, lede, limit = 4, request_source = "deal" } = props;
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/deals", { params: { sort: "ending" } }).then((r) => setDeals((Array.isArray(r.data) ? r.data : r.data?.items || []).slice(0, limit))).catch(() => {}); getCategories().then(setCats); }, [limit]);
  if (!deals.length) return mode === "edit" ? <div className="py-10 text-center text-sm text-slate-400">Deals appear here when providers publish them.</div> : null;
  const open = (d) => {
    trackHome("deal_view", { deal: d.id });
    const c = cats.find((x) => x.id === d.service?.category_id);
    const ctx = c?.parent_id ? { category_id: c.parent_id, subcategory_id: c.id } : { category_id: c?.id };
    navigate(buildRequestUrl({ ...ctx, deal_id: d.id, service_id: d.service_id, provider_id: d.provider_id, request_source, homepage_component: "deals", search_text: `I'd like the "${d.title}" deal` }));
  };
  return (
    <section className="bg-white py-16 md:py-24" data-testid="request-deals">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <SectionHeader eyebrow={eyebrow} headline={headline} lede={lede} />
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {deals.map((d) => (
            <div key={d.id} className="rounded-3xl bg-[#f5f0e8] p-6 flex flex-col" data-testid={`request-deal-${d.id}`}>
              <div className="inline-flex w-fit items-center gap-1.5 rounded-full bg-[#165a71] px-3 py-1 text-xs font-semibold text-white"><Tag className="h-3.5 w-3.5" />{d.discount_type === "percent" ? `${d.discount_value}% off` : `$${d.discount_value} off`}</div>
              <div className="mt-4 font-display text-lg font-semibold text-[#071b22]">{d.title}</div>
              <div className="mt-1 text-sm text-slate-500 flex-1">{d.service?.title}{d.provider?.name ? ` · ${d.provider.name}` : ""}</div>
              <button onClick={() => open(d)} className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-[#071b22] px-5 py-2.5 text-sm font-semibold text-white hover:bg-black" data-testid={`request-deal-btn-${d.id}`}>Request This Service <ArrowRight className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function RequestCta({ props = {}, mode }) {
  const { heading, description, icon, button_label = "Request a Service", category_slug, urgency, deal_id, campaign, tone = "sand", request_source = "cta" } = props;
  const navigate = useNavigate();
  const dark = tone === "dark";
  const click = async () => {
    if (mode === "edit") return;
    trackHome("request_click", { source: request_source, category: category_slug });
    const ctx = category_slug ? await contextForSlug(category_slug) : {};
    navigate(buildRequestUrl({ ...ctx, urgency, deal_id, campaign, request_source, homepage_component: heading }));
  };
  return (
    <section className={cx(TONES[tone] || TONES.sand, "py-12 md:py-16")} data-testid="request-cta">
      <div className="mx-auto max-w-5xl px-5 lg:px-10 flex flex-col md:flex-row md:items-center gap-6">
        <div className={cx("h-14 w-14 shrink-0 rounded-2xl grid place-items-center", dark ? "bg-white/10 text-[#87bccc]" : "bg-[#165a71]/10 text-[#165a71]")}><DynIcon name={icon} className="h-6 w-6" /></div>
        <div className="flex-1">
          <div className={cx("font-display text-2xl font-semibold", dark ? "text-white" : "text-[#071b22]")}>{heading}</div>
          {description && <p className={cx("mt-1", dark ? "text-white/70" : "text-slate-600")}>{description}</p>}
        </div>
        <button onClick={click} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] px-6 py-3.5 text-sm font-semibold text-white hover:bg-[#0f4657]" data-testid="request-cta-btn">{button_label} <ArrowRight className="h-4 w-4" /></button>
      </div>
    </section>
  );
}
